import { goalbookUrls } from '@/links'
export { setupHandleRequest, getHandleRequest, createHandleRequest, refreshJwtToken, getCookie, setCrossSiteLogin, handleRequest, parseJsonResponse, getCSRFHeaders, getOrStartRefresh }

// Track pending refresh token request
let pendingRefreshPromise = null

// Module-level variable to store the request handler
let handleRequestInstance = null

// Setup the request handler with a login function
function setupHandleRequest(loginFunction) {
  if (!loginFunction) {
    handleRequestInstance = null
    return null
  }
  handleRequestInstance = createHandleRequest(loginFunction)
  return handleRequestInstance
}

// Get the current request handler
function getHandleRequest() {
  return handleRequestInstance || null
}

function createHandleRequest(loginFn) {
  return async function({ requestFn, refreshFn = refreshJwtToken }, ...args) {
    const context = {
      requestFn,
      refreshFn,
      loginFn,
      args
    }
    return handleRequest(context)
  }
}

async function handleRequest(context) {
  // Make the original request
  const response = await context.requestFn(...context.args)

  if (response instanceof Response) {
    // Credentials are missing or invalid. Refresh tokens or reauthenticate.
    if (response.status === 401) {
      return handleAuthenticationError(context)
    }
    // For success, or other error status codes, parse and return the response.
    return parseJsonResponse(response)
  }

  // If response is already parsed JSON, return it
  return response
}

// Handle authentication errors by refreshing tokens or reauthenticating
async function handleAuthenticationError(context) {
  // Ensure only a single refresh request is in-flight (single-flight)
  const refreshRes = await getOrStartRefresh(context.refreshFn)

  if (refreshRes.ok) {
    return handleRefreshTokenSuccess(context)
  }

  // If token refresh failed, try to reauthenticate
  try {
    const status = await context.loginFn()
    if (status.message === 'success') {
      return handleRequest(context) // Retry original request
    } else {
      throw {
        status_code: 401,
        message: 'Failed to reauthenticate: login unsuccessful',
        status: 'error'
      }
    }
  } catch (e) {
    // If the error is already an ErrorResponse, rethrow it
    if (e.status_code === 401 && e.status === 'error') {
      throw e
    }
    // Otherwise wrap it in our error format
    throw {
      status_code: 401,
      message: 'Failed to reauthenticate: error with login',
      status: 'error'
    }
  }
}

// Create or return the single in-flight refresh promise and clear it on completion
function getOrStartRefresh(refreshFn) {
  if (!pendingRefreshPromise) {
    // Immediately assign the promise synchronously to avoid races before any awaits
    pendingRefreshPromise = (async () => {
      try {
        return await refreshFn()
      } finally {
        // Clear the pending promise once the refresh completes (success or failure)
        pendingRefreshPromise = null
      }
    })()
  }
  return pendingRefreshPromise
}

// If token refresh was successful, retry the original request
async function handleRefreshTokenSuccess(context) {
  const response = await context.requestFn(...context.args)
  if (response instanceof Response) {
    return parseJsonResponse(response)
  }
  return response
}

async function parseJsonResponse(response) {
  let res

  // Clone the response since we might need to read the body multiple times
  const clonedResponse = response.clone()

  try {
    res = await response.json()
  } catch {
    // If JSON parsing fails, get the text content or status text as fallback
    try {
      const text = await clonedResponse.text()
      res = { message: text || response.statusText || 'Unknown error' }
    } catch {
      res = { message: response.statusText || 'Unknown error' }
    }
  }

  if (response.status >= 400) {
    const error = {
      status_code: response.status,
      message: res.message || 'Unknown error',
      status: 'error'
    }
    throw error
  }

  return res
}

async function refreshJwtToken() {
  const response = await fetch(`${goalbookUrls.accounts}/auth/refresh`, {
    credentials: 'include',
    method: 'POST'
  })
  return response
}

async function setCrossSiteLogin() {
  const response = await fetch(`${goalbookUrls.accounts}/users/sign_in`, {
    credentials: 'include',
    method: 'GET'
  })
  return response
}

function getCookie(name) {
  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith(name + '='))
      ?.split('=')[1] || ''
  )
}

function getCSRFHeaders() {
  const gbcs = getCookie('gbcs')
  return new Headers({
    'Content-Type': 'application/json',
    'X-CSRF-Token': gbcs,
    'X-Requested-With': 'XMLHttpRequest'
  })
}
